import React, { useMemo } from 'react';
import { animated, useSpring } from 'react-spring';
import { usePrefersReducedMotion } from '@shared/hooks/usePrefersReducedMotion';
import { StyledProgressBar, StyledProgressBarFill, styles } from './styles';
import type { BoxProps } from '@withjoy/joykit';

export interface ProgressBarProps {
  progressPercentage: number;
  height?: number;
  backgroundColor?: BoxProps['color'];
  fillColor?: BoxProps['color']; // For solid colors
  gradientFill?: string; // For gradients
  /** Set to false to skip the initial grow-to-fill animation. Default: true */
  animate?: boolean;
  /**
   * Exposes the bar to assistive technology as a progressbar, such as "Step 2 of 5". Without it the
   * bar is decoration, for callers that say the same thing in text.
   */
  accessibleProgress?: Readonly<{ label: string; now: number; max: number }>;
  /** Fills on this spring instead of the standard 150ms ease. With reduced motion the fill jumps. */
  fillSpring?: Readonly<{ tension: number; friction: number }>;
}

const AnimatedProgressBarFill = animated(StyledProgressBarFill);

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progressPercentage,
  height = 3,
  backgroundColor,
  fillColor,
  gradientFill,
  animate = true,
  accessibleProgress,
  fillSpring,
}) => {
  const controlledPercentage = useMemo(() => {
    if (progressPercentage < 0) return 0;
    if (progressPercentage > 100) return 100;
    return progressPercentage;
  }, [progressPercentage]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const springFill = useSpring({ width: controlledPercentage, immediate: !fillSpring || prefersReducedMotion, config: fillSpring });

  return (
    <StyledProgressBar
      __css={styles.progressBar(height, backgroundColor)}
      {...(accessibleProgress && {
        role: 'progressbar',
        'aria-label': accessibleProgress.label,
        'aria-valuemin': 0,
        'aria-valuemax': accessibleProgress.max,
        'aria-valuenow': accessibleProgress.now,
      })}
    >
      {/* inline style is set to update the width without the creation of a new style rule */}
      {fillSpring ? (
        <AnimatedProgressBarFill
          $animate={animate}
          __css={{ ...styles.progressBarFill(height, fillColor, gradientFill), transition: 'none' }}
          style={{ width: springFill.width.interpolate(width => `${width}%`) }}
        />
      ) : (
        <StyledProgressBarFill
          $animate={animate}
          __css={styles.progressBarFill(height, fillColor, gradientFill)}
          style={{ width: `${controlledPercentage}%` }}
        />
      )}
    </StyledProgressBar>
  );
};
